// ═══════════════════════════════════════════════════════════════════════════════
// Home Stock wizard — photo ↔ item linkage helpers
// ═══════════════════════════════════════════════════════════════════════════════

import type { InventoryLevel } from '@fdh/shared';

export type StocktakeLineState = {
  itemName: string;
  sortOrder: number;
  status: InventoryLevel | null;
  existingPhotoUrl: string;
  everMatchedByAi: boolean;
  aiRaw: Record<string, unknown>;
  useNoExpiry: boolean | null;
  expiryYmdList: string[];
};

export type StocktakePhotoItem = {
  id: string;
  displayUrl: string;
  canonicalUrl: string;
  aiAnalysisCompleted: boolean;
};

export const clearedLineStateForPhotoReset = (): Pick<
  StocktakeLineState,
  'status' | 'existingPhotoUrl' | 'everMatchedByAi' | 'aiRaw' | 'useNoExpiry' | 'expiryYmdList'
> => ({
  status: null,
  existingPhotoUrl: '',
  everMatchedByAi: false,
  aiRaw: {},
  useNoExpiry: null,
  expiryYmdList: [],
});

export const countLinesLinkedToCanonical = (
  lineMap: Record<string, StocktakeLineState>,
  canonicalUrl: string,
): number => {
  const target = canonicalUrl.trim();
  if (target === '') {
    return 0;
  }
  return Object.values(lineMap).filter((st) => st.existingPhotoUrl.trim() === target).length;
};

export const collectManualIdsLinkedToCanonical = (
  lineMap: Record<string, StocktakeLineState>,
  manualIds: Set<string>,
  canonicalUrl: string,
): string[] => {
  const target = canonicalUrl.trim();
  return Object.keys(lineMap).filter(
    (id) => manualIds.has(id) && (lineMap[id]?.existingPhotoUrl.trim() ?? '') === target,
  );
};

type ManualRowIntroRef = {
  itemId: string;
  introducedLocation?: string;
};

/** Manual template rows scoped to a location but removed from lineMap (e.g. after photo purge). */
export const collectOrphanManualRowIdsForLocation = (
  manualRows: ManualRowIntroRef[],
  manualIds: Set<string>,
  wizardLocs: string[],
  locationName: string,
  lineMap: Record<string, StocktakeLineState>,
): string[] =>
  manualRows
    .filter((row) => {
      if (!manualIds.has(row.itemId) || lineMap[row.itemId]) {
        return false;
      }
      const intro = row.introducedLocation?.trim() ?? '';
      if (wizardLocs.length <= 1) {
        return true;
      }
      if (intro !== '') {
        return intro === locationName;
      }
      return (wizardLocs[0] ?? '') === locationName;
    })
    .map((row) => row.itemId);

export const purgeLineDataForCanonicalPhoto = (
  lineMap: Record<string, StocktakeLineState>,
  manualIds: Set<string>,
  canonicalUrl: string,
): { lineMap: Record<string, StocktakeLineState>; manualIdsToRemove: string[] } => {
  const target = canonicalUrl.trim();
  const manualIdsToRemove: string[] = [];
  if (target === '') {
    return { lineMap, manualIdsToRemove };
  }
  const next: Record<string, StocktakeLineState> = { ...lineMap };
  for (const key of Object.keys(next)) {
    const st = next[key];
    if (!st || st.existingPhotoUrl.trim() !== target) {
      continue;
    }
    if (manualIds.has(key)) {
      manualIdsToRemove.push(key);
      delete next[key];
      continue;
    }
    next[key] = {
      ...st,
      ...clearedLineStateForPhotoReset(),
    };
  }
  return { lineMap: next, manualIdsToRemove };
};

const EXPIRY_YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Subtitle for multi-location picker: why a location is not yet "Completed". */
export const resolveLocationHubStatusLineKey = (params: {
  hasPhoto: boolean;
  allRowsPresent: boolean;
  readyForReview: boolean;
  lineMap: Record<string, StocktakeLineState>;
  rowsHere: { itemId: string }[];
  wizardCategories: string[];
  noItemsCategories: string[];
  getRowCategory: (itemId: string) => string;
}): string => {
  const {
    hasPhoto,
    allRowsPresent,
    readyForReview,
    lineMap,
    rowsHere,
    wizardCategories,
    noItemsCategories,
    getRowCategory,
  } = params;

  if (readyForReview) {
    return 'inventory.wizard.locationCompletedLine';
  }
  if (!hasPhoto) {
    return 'inventory.wizard.tapToStart';
  }
  if (!allRowsPresent) {
    return 'inventory.wizard.locationHubStatusInProgress';
  }

  const noSet = new Set(noItemsCategories);
  for (const row of rowsHere) {
    if (noSet.has(getRowCategory(row.itemId))) {
      continue;
    }
    const st = lineMap[row.itemId];
    if (!st || st.status === null) {
      return 'inventory.wizard.locationHubStatusNeedsStock';
    }
    if (st.useNoExpiry === null) {
      return 'inventory.wizard.locationHubStatusNeedsExpiry';
    }
    if (st.useNoExpiry === false) {
      const hasValidDate = (st.expiryYmdList ?? []).some((d) => EXPIRY_YMD_REGEX.test(d.trim()));
      if (!hasValidDate) {
        return 'inventory.wizard.locationHubStatusNeedsExpiry';
      }
    }
  }

  if (wizardCategories.length > 0) {
    for (const cat of wizardCategories) {
      if (noSet.has(cat)) {
        continue;
      }
      const rowsInCat = rowsHere.filter((r) => getRowCategory(r.itemId) === cat);
      if (rowsInCat.length === 0) {
        return 'inventory.wizard.locationHubStatusNeedsCategories';
      }
    }
  }

  return 'inventory.wizard.locationHubStatusInProgress';
};

/** Remove categories from a location's "no items here" tick list (immutable). */
export const removeCategoriesFromNoItemsList = (
  noItemsCategories: readonly string[],
  categoriesToRemove: readonly string[],
): string[] => {
  const removeSet = new Set(
    categoriesToRemove.map((c) => c.trim()).filter((c) => c !== ''),
  );
  if (removeSet.size === 0) {
    return [...noItemsCategories];
  }
  return noItemsCategories.filter((c) => !removeSet.has(c));
};

export const applyFirstPhotoFallbackToLines = (
  lineMap: Record<string, StocktakeLineState>,
  firstCanonicalUrl: string,
): Record<string, StocktakeLineState> => {
  const first = firstCanonicalUrl.trim();
  if (first === '') {
    return lineMap;
  }
  const next: Record<string, StocktakeLineState> = { ...lineMap };
  for (const key of Object.keys(next)) {
    const st = next[key];
    if (!st || st.status === null) {
      continue;
    }
    if (st.existingPhotoUrl.trim() === '') {
      next[key] = { ...st, existingPhotoUrl: first };
    }
  }
  return next;
};
